import React from 'react'
import { twMerge } from 'tailwind-merge'
import { Link as RouterLink } from 'react-router-dom'
import { ActivityIndicator } from './ActivityIndicator'
import type {
    ButtonHTMLAttributes,
    InputHTMLAttributes,
    ReactNode,
    AnchorHTMLAttributes,
    TextareaHTMLAttributes,
    SelectHTMLAttributes,
} from 'react'

// ============================================================================
// TYPOGRAPHY COMPONENTS
// Mobile-first, responsive, with light/dark mode variants
// ============================================================================

interface TypographyProps {
    children: ReactNode
    className?: string
    variant?: 'light' | 'dark'
    id?: string
    role?: string
}

/**
 * Display - Largest text for hero sections, landing pages
 * Mobile: 4xl, Tablet: 5xl, Desktop: 6xl
 */
export const Display = ({ children, className = '' }: TypographyProps) => (
    <h1
        className={twMerge(
            `
            text-4xl sm:text-5xl md:text-6xl lg:text-7xl
            font-black
            tracking-tight
            leading-tight
            font-heading
            text-[var(--dhtn-text)]
        `,
            className
        )}
    >
        {children}
    </h1>
)

/**
 * H1 - Primary headings (page titles)
 * Mobile: 3xl, Tablet: 4xl, Desktop: 5xl
 */
export const H1 = ({ children, className = '' }: TypographyProps) => (
    <h1
        className={twMerge(
            `
            text-3xl sm:text-4xl md:text-5xl lg:text-6xl
            font-extrabold
            tracking-normal
            leading-tight
            font-heading
            text-[var(--dhtn-text)]
        `,
            className
        )}
    >
        {children}
    </h1>
)

/**
 * H2 - Secondary headings (section titles)
 * Mobile: 2xl, Tablet: 3xl, Desktop: 4xl
 */
export const H2 = ({ children, className = '', id }: TypographyProps) => (
    <h2
        id={id}
        className={twMerge(
            `
            text-2xl sm:text-3xl md:text-4xl lg:text-5xl
            font-bold
            tracking-normal
            leading-tight
            font-heading
            text-[var(--dhtn-text)]
        `,
            className
        )}
    >
        {children}
    </h2>
)

/**
 * H3 - Tertiary headings (card titles, subsections)
 * Mobile: xl, Tablet: 2xl, Desktop: 3xl
 */
export const H3 = ({ children, className = '', id }: TypographyProps) => (
    <h3
        id={id}
        className={twMerge(
            `
            text-xl sm:text-2xl md:text-3xl
            font-bold
            tracking-normal
            font-heading
            text-[var(--dhtn-text)]
        `,
            className
        )}
    >
        {children}
    </h3>
)

/**
 * H4 - Small headings (form sections, small cards)
 * Mobile: lg, Tablet: xl, Desktop: 2xl
 */
export const H4 = ({ children, className = '' }: TypographyProps) => (
    <h4
        className={twMerge(
            `
            text-lg sm:text-xl md:text-2xl
            font-semibold
            tracking-normal
            font-heading
            text-[var(--dhtn-text)]
        `,
            className
        )}
    >
        {children}
    </h4>
)

/**
 * BodyXl - Large body text (introductions, important paragraphs)
 * Mobile: lg, Tablet: xl, Desktop: 2xl
 */
export const BodyXl = ({ children, className = '' }: TypographyProps) => (
    <p
        className={twMerge(
            `
            text-lg sm:text-xl md:text-2xl
            font-medium
            leading-relaxed
            font-sans
            text-[var(--dhtn-text)]
        `,
            className
        )}
    >
        {children}
    </p>
)

/**
 * Body - Standard body text
 * Mobile: base (16px) — readable on all screens
 * Desktop: lg (18px) — comfortable reading size
 */
export const Body = ({
    children,
    className = '',
    id,
    role,
}: TypographyProps) => (
    <p
        id={id}
        role={role}
        className={twMerge(
            `
            text-base md:text-lg
            font-medium
            leading-relaxed
            font-sans
            text-[var(--dhtn-text)]
        `,
            className
        )}
    >
        {children}
    </p>
)

/**
 * BodySm - Small body text (card descriptions, secondary content)
 * Mobile: sm (14px)
 * Desktop: base (16px)
 */
export const BodySm = ({
    children,
    className = '',
    id,
    role,
}: TypographyProps) => (
    <p
        id={id}
        role={role}
        className={twMerge(
            `
            text-sm md:text-base
            font-medium
            leading-relaxed
            font-sans
            text-[var(--dhtn-text-muted)]
        `,
            className
        )}
    >
        {children}
    </p>
)

/**
 * Caption - Small text for captions, hints, helper text
 * Mobile: xs, Tablet: sm
 */
export const Caption = ({ children, className = '' }: TypographyProps) => (
    <p
        className={twMerge(
            `
            text-xs sm:text-sm
            font-normal
            text-[var(--dhtn-text-muted)]
        `,
            className
        )}
    >
        {children}
    </p>
)

/**
 * Label - Form labels, field labels
 * Mobile: sm, Tablet: base
 */
export const Label = ({ children, className = '' }: TypographyProps) => (
    <span
        className={twMerge(
            `
            text-sm sm:text-base
            font-medium
            text-[var(--dhtn-text)]
        `,
            className
        )}
    >
        {children}
    </span>
)

/**
 * Stat - For displaying statistics, numbers, metrics
 * Mobile: 2xl, Tablet: 3xl, Desktop: 4xl
 */
export const Stat = ({ children, className = '' }: TypographyProps) => (
    <div
        className={twMerge(
            `
            text-2xl sm:text-3xl md:text-4xl
            font-extrabold
            tracking-tight
            text-[var(--dhtn-text)]
        `,
            className
        )}
    >
        {children}
    </div>
)

/**
 * StatLabel - Labels for statistics
 * Mobile: sm, Tablet: base
 */
export const StatLabel = ({ children, className = '' }: TypographyProps) => (
    <span
        className={twMerge(
            `
            text-sm sm:text-base
            font-medium
            text-[var(--dhtn-text-muted)]
        `,
            className
        )}
    >
        {children}
    </span>
)

// ============================================================================
// BUTTON COMPONENTS
// Duolingo-style 3D buttons with proper press effects
// ============================================================================

const BUTTON_BASE = 'inline-flex items-center justify-center font-semibold whitespace-nowrap transition-none relative select-none'
// Actual pixels: the 15.2px root makes rem-based tap targets smaller than 44px.
// Labels use the same 18px/600 typography for both buttons and navigation links.
const BUTTON_SIZES = {
    sm: 'min-h-[44px] px-6 text-[16px] leading-5 rounded-control',
    md: 'min-h-[48px] px-8 text-[18px] leading-6 rounded-control',
    lg: 'min-h-[52px] px-8 min-w-[200px] text-[18px] leading-6 rounded-control',
}
const primaryButtonClasses = (link = false) => `
    border-2 border-transparent
    bg-[var(--dhtn-action)] text-[var(--dhtn-action-text)]
    shadow-[0_4px_0_var(--dhtn-action-depth)]
    hover:border-transparent hover:bg-[var(--dhtn-action-hover)]
    ${link ? 'active:translate-y-[4px] active:shadow-none' : 'enabled:active:translate-y-[4px] enabled:active:shadow-none'}
`

/** A navigation link with the primary Button's sizing, fill and press behavior. `to` stays in the app; `href` loads a page. */
export const ButtonLink = React.forwardRef<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement> & {
    size?: 'sm' | 'md' | 'lg'
    fullWidth?: boolean
    to?: string
}>(({ size = 'lg', fullWidth = false, className = '', to, ...props }, ref) => {
    const classes = twMerge(BUTTON_BASE, primaryButtonClasses(true), BUTTON_SIZES[size], fullWidth ? 'w-full' : '', className)
    return to ? <RouterLink ref={ref} to={to} data-dhtn-action="" className={classes} {...props} />
        : <a ref={ref} data-dhtn-action="" className={classes} {...props} />
})
ButtonLink.displayName = 'ButtonLink'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?:
        | 'primary'
        | 'primaryDark'
        | 'primaryBlack'
        | 'blue'
        | 'blueDark'
        | 'amber'
        | 'ghost'
        | 'ghostDark'
        | 'danger'
        | 'white'
        | 'whiteDark'
        | 'success'
        | 'successDark'
    size?: 'sm' | 'md' | 'lg'
    fullWidth?: boolean
    isLoading?: boolean
    loadingText?: string
}

interface SecondaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?:
        | 'secondary'
        | 'secondaryDark' // existing gray ones
        | 'secondaryGreen'
        | 'secondaryRose' // colored variants
        | 'secondaryBlue'
        | 'secondaryOrange' // with white text
        | 'secondaryPink'
        | 'secondaryOnColor' // white text + white border, for any colored card background
    size?: 'sm' | 'md' | 'lg'
    fullWidth?: boolean
    isLoading?: boolean
    loadingText?: string
}

/**
 * ButtonLoading - a loading button goes FLAT + DIMMED (no 3D lip) and shows the
 * ActivityIndicator in place of its label. The label stays in the layout, invisible,
 * so the button keeps its exact width and height; the indicator sits centred over it.
 * The indicator inherits `currentColor`, so a primary shows white spokes and a
 * secondary shows its own ink. 20px on md/lg, 16px on sm.
 */
const ButtonLoading = ({ children, label, size }: { children: ReactNode; label?: string; size: 'sm' | 'md' | 'lg' }) => (
    <>
        <span className="invisible inline-flex items-center justify-center gap-[inherit]" aria-hidden="true">{children}</span>
        <ActivityIndicator
            size={size === 'sm' ? 16 : 20}
            label={label}
            className="absolute inset-0"
        />
    </>
)

/**
 * Button - Primary action button (green by default)
 * - Mobile-first sizing
 * - 3D shadow effect
 * - Press animation
 * - Loading state
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            variant = 'primary',
            size = 'lg',
            fullWidth = false,
            isLoading = false,
            loadingText,
            className = '',
            ...props
        },
        ref
    ) => {
        // A disabled brand button gets its own muted ultramarine, not a 50% fade: faded over white,
        // ultramarine reads lilac (founder, 26 Sep 2026). Other variants still fade.
        const brandFill = variant === 'primary' || variant === 'primaryDark' || variant === 'blue' || variant === 'blueDark'
        const baseClasses = `
      ${BUTTON_BASE}
      ${fullWidth ? 'w-full' : ''}
      ${isLoading ? 'cursor-wait' : ''}
      ${props.disabled ? (brandFill ? 'cursor-not-allowed' : 'opacity-50 cursor-not-allowed') : ''}
    `

        // EVERY press rule below is `enabled:active:`, never bare `active:`.
        //
        // A disabled button must be visually inert on press — no travel, no lip change. Measured on
        // /dev/atoms 2026-07-31: a genuinely disabled `primary` still ran `active:translate-y-[4px]`
        // and moved 4px down under the finger (transform `none` -> `matrix(1,0,0,1,0,4)`). Its lip
        // was already gone, because the disabled branch at the bottom of this component adds
        // `shadow-none` — so the button dropped into its own empty shadow and looked pressed while
        // doing nothing. That is the founder's "if I click it, I shouldn't see that animation of
        // transform coming down."
        //
        // `:enabled` does not match a disabled control, so the rule never applies and there is
        // nothing to counteract. It only matches form elements, which is fine — these are buttons.
        // Same fix, same reasoning, in lessons/tapTarget.ts.
        //
        // THE BRAND RULE (full table over --dhtn-action in index.css): paint with --dhtn-action,
        // put --dhtn-action-depth under it, write TEXT in --dhtn-action-ink. No other blue.
        const variants = {
            primary: primaryButtonClasses(),
            // Byte-identical to `primary` — both are token-driven, so the theme already does what
            // the `Dark` suffix used to. Kept only so existing call sites keep compiling.
            primaryDark: primaryButtonClasses(),
            primaryBlack: `
                border-2 border-transparent
                bg-black text-white
                shadow-[0_4px_0_#384054]
                hover:border-transparent hover:bg-[#131721]
                enabled:active:translate-y-[4px]
                enabled:active:shadow-none
            `,
            // `blue` is the same action colour as `primary`; the name is legacy (zero call sites).
            blue: `
                bg-[var(--dhtn-action)] text-[var(--dhtn-action-text)]
                shadow-[0_4px_0_var(--dhtn-action-depth)]
                hover:brightness-105
                enabled:active:translate-y-[4px]
                enabled:active:shadow-none
            `,
            blueDark: `
                bg-[var(--dhtn-action)] text-[var(--dhtn-action-text)]
                shadow-[0_4px_0_var(--dhtn-action-depth)]
                hover:brightness-105
                enabled:active:translate-y-[4px]
                enabled:active:shadow-none
            `,
            // Warning is a light fill, so its label is dark (8.86:1). White on it was 2.15:1.
            amber: `
                bg-[var(--dhtn-warning)] text-[var(--dhtn-on-warning)]
                shadow-[0_4px_0_theme(colors.warning.700)]
                hover:brightness-105
                enabled:active:translate-y-[4px]
                enabled:active:shadow-none
            `,
            ghost: `
                bg-transparent text-gray-600
                hover:text-gray-900 hover:bg-[var(--dhtn-hover)]
                enabled:active:bg-gray-200
            `,
            ghostDark: `
                bg-transparent text-gray-300
                hover:text-white hover:bg-[var(--dhtn-hover)]
                enabled:active:bg-dark-700
            `,
            danger: `
                bg-[var(--dhtn-error)] text-white
                shadow-[0_4px_0_theme(colors.error.700)]
                hover:brightness-105
                enabled:active:translate-y-[4px]
                enabled:active:shadow-none
            `,
            // A white button with brand ink, for sitting ON a brand/photo hero. primary.700 on white
            // is 12.45:1 and matches the lip beneath it.
            // NOTE it is a raw palette step and NOT --dhtn-action-ink on purpose. This variant
            // paints its own white ground in both themes, so the theme-split token would resolve
            // to the dark ink #3E7DFF in dark and put 2.9:1 text on white. The ink token follows
            // the CANVAS; this button is not on the canvas.
            white: `
                bg-white text-primary-700
                shadow-[0_4px_0_theme(colors.primary.700)]
                hover:bg-primary-50
                enabled:active:translate-y-[4px]
                enabled:active:shadow-none
            `,
            whiteDark: `
                border-2 border-transparent
                bg-white text-[#0E1014]
                shadow-[0_4px_0_#B4B8C2]
                hover:bg-gray-50
                enabled:active:translate-y-[4px]
                enabled:active:shadow-none
            `,
            success: `
                bg-[var(--dhtn-success)] text-white
                shadow-[0_4px_0_theme(colors.success.700)]
                hover:brightness-105
                enabled:active:translate-y-[4px]
                enabled:active:shadow-none
            `,
            successDark: `
                bg-[var(--dhtn-success)] text-white
                shadow-[0_4px_0_theme(colors.success.700)]
                hover:brightness-105
                enabled:active:translate-y-[4px]
                enabled:active:shadow-none
            `,
        }

        return (
            <button
                ref={ref}
                data-dhtn-action=""
                className={twMerge(`
          ${baseClasses}
          ${variants[variant]}
          ${BUTTON_SIZES[size]}
          ${props.disabled ? (brandFill ? 'bg-[var(--dhtn-action-disabled)] hover:bg-[var(--dhtn-action-disabled)] text-[var(--dhtn-action-disabled-text)] shadow-none' : 'opacity-50 shadow-none') : ''}
          ${isLoading ? 'shadow-none' : ''}
        `, className)}
                {...props}
                disabled={isLoading || props.disabled}
                aria-busy={isLoading || props['aria-busy']}
            >
                {isLoading ? (
                    <ButtonLoading label={loadingText} size={size}>{children}</ButtonLoading>
                ) : (
                    children
                )}
            </button>
        )
    }
)

Button.displayName = 'Button'

/**
 * SecondaryButton - Secondary action button (outline style)
 * - Lighter visual weight
 * - Subtle 3D effect
 * - Perfect for "Cancel", "Back", etc.
 */
export const SecondaryButton = React.forwardRef<HTMLButtonElement, SecondaryButtonProps>(
    (
        {
            children,
            variant = 'secondary',
            size = 'lg',
            fullWidth = false,
            isLoading = false,
            loadingText,
            className = '',
            ...props
        },
        ref
    ) => {
        const baseClasses = `
      inline-flex items-center justify-center
      font-semibold
      whitespace-nowrap
      transition-none
      transform
      ${fullWidth ? 'w-full' : ''}
      ${isLoading ? 'cursor-wait' : ''}
      ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}
      relative
      select-none
    `

        const variants = {
            // The 2px lip is the BORDER seen edge-on, not a shadow behind the button —
            // so it has to move with the border on hover. Tinting only the outline left
            // the 3D edge a paler colour underneath, which reads as the button coming
            // apart. Border and lip now change together, in one step.
            // The LABEL uses --dhtn-action-ink (7.62:1 light; white in dark), the BORDER is untouched.
            secondary: `
                bg-transparent text-[var(--dhtn-action-ink)]
                border-2 border-[var(--dhtn-control-edge)]
                shadow-[0_2px_0_var(--dhtn-control-edge)]
                hover:bg-[var(--dhtn-hover)]
                hover:border-[var(--dhtn-control-edge-hover)]
                hover:shadow-[0_2px_0_var(--dhtn-control-edge-hover)]
                enabled:active:translate-y-[2px]
                enabled:active:shadow-none
            `,
            // One secondary style in both themes (founder, 9 Oct 2026): the old dark variant drew its border
            // and lip at a 12% tint, so the lip vanished and only the border changed on hover. It now uses the
            // same solid control edge as `secondary` (the tokens already switch with the theme).
            secondaryDark: `
                bg-transparent text-[var(--dhtn-action-ink)]
                border-2 border-[var(--dhtn-control-edge)]
                shadow-[0_2px_0_var(--dhtn-control-edge)]
                hover:bg-[var(--dhtn-hover)]
                hover:border-[var(--dhtn-control-edge-hover)]
                hover:shadow-[0_2px_0_var(--dhtn-control-edge-hover)]
                enabled:active:translate-y-[2px]
                enabled:active:shadow-none
            `,
            // Colored variants - transparent background, white text, matching border & shadow.
            // These sit on a coloured card, so the white label is on the CARD, not on white.
            //
            // `secondaryGreen` is the action colour; the name is legacy (live in AdminStudentDetail).
            secondaryGreen: `
                bg-transparent text-white
                border-2 border-[var(--dhtn-action)]
                shadow-[0_2px_0_var(--dhtn-action)]
                hover:bg-primary-500 hover:bg-opacity-[30%]
                enabled:active:translate-y-[2px]
                enabled:active:shadow-none
            `,
            secondaryRose: `
                bg-transparent text-white
                border-2 border-rose-600
                shadow-[0_2px_0_theme(colors.rose.600)]
                hover:bg-rose-600 hover:bg-opacity-[30%]
                enabled:active:translate-y-[2px]
                enabled:active:shadow-none
            `,
            // Was Tailwind blue-600 (#2563eb). Same story as `blue` above: not our blue at all.
            // The hover keeps a palette class rather than the token because `bg-opacity-*` needs
            // a colour Tailwind can split into `rgb(… / var(--tw-bg-opacity))`, and it cannot do
            // that to a `var()`. primary.500 IS --dhtn-action's light value (#0B5CFF) — see the note on
            // secondaryOnColor for what happens when an opacity shorthand silently drops.
            secondaryBlue: `
                bg-transparent text-white
                border-2 border-[var(--dhtn-action)]
                shadow-[0_2px_0_var(--dhtn-action)]
                hover:bg-primary-500 hover:bg-opacity-[30%]
                enabled:active:translate-y-[2px]
                enabled:active:shadow-none
            `,
            secondaryOrange: `
                bg-transparent text-white
                border-2 border-orange-600
                shadow-[0_2px_0_theme(colors.orange.600)]
                hover:bg-orange-600 hover:bg-opacity-[30%]
                enabled:active:translate-y-[2px]
                enabled:active:shadow-none
            `,
            secondaryPink: `
                bg-transparent text-white
                border-2 border-pink-600
                shadow-[0_2px_0_theme(colors.pink.600)]
                hover:bg-pink-600 hover:bg-opacity-[30%]
                enabled:active:translate-y-[2px]
                enabled:active:shadow-none
            `,
            // `shadow-[0_2px_0_white/40]` was NOT a lip — Tailwind's slash-opacity shorthand does
            // not work inside an arbitrary value, so this emitted `box-shadow: 0 2px 0 white/40`,
            // which is invalid CSS and dropped whole. The button had a 40%-white border and no
            // lip under it at all, in every state. Written as rgba it matches the border exactly.
            secondaryOnColor: `
                bg-white/10 text-white
                border-2 border-white/40
                shadow-[0_2px_0_rgba(255,255,255,0.4)]
                hover:bg-white/20
                enabled:active:translate-y-[2px]
                enabled:active:shadow-none
            `,
        }

        const sizes = {
            // 44px, not min-h-11 (41.8px at this app's 15.2px root). Same note as Button's sizes.
            sm: '!min-h-[44px] !px-6 text-[16px] leading-5 rounded-control',
            md: '!min-h-[50px] !px-8 text-[18px] leading-6 rounded-control',
            lg: '!min-h-[55px] !px-8 !min-w-[200px] text-[18px] leading-6 rounded-control',
        }

        return (
            <button
                ref={ref}
                data-dhtn-action=""
                className={twMerge(`
          ${baseClasses}
          ${variants[variant]}
          ${sizes[size]}
          ${isLoading || props.disabled ? 'shadow-none' : ''}
        `, className)}
                {...props}
                disabled={isLoading || props.disabled}
                aria-busy={isLoading || props['aria-busy']}
            >
                {isLoading ? (
                    <ButtonLoading label={loadingText} size={size}>{children}</ButtonLoading>
                ) : (
                    children
                )}
            </button>
        )
    }
)

SecondaryButton.displayName = 'SecondaryButton'

// ============================================================================
// LOADING COMPONENTS
// Clear circular loading states
// ============================================================================

/**
 * LoadingSpinner - the ActivityIndicator at a named size and a named ink.
 * Kept as a thin wrapper so existing call sites need no edits.
 * Progress, not text, so the brand spinner takes --dhtn-action-mark (it stays blue in dark,
 * where the text ink is white). Neutral -> --dhtn-text-muted (the Apple grey).
 */
export const LoadingSpinner = ({
    size = 'md',
    color = 'primary',
    label,
}: {
    size?: 'sm' | 'md' | 'lg'
    color?: 'primary' | 'white' | 'gray' | 'actionText' | 'text'
    label?: string
}) => {
    const px = { sm: 20, md: 24, lg: 32 }[size]
    const ink =
        color === 'white' ? 'text-white' :
        color === 'actionText' ? 'text-[var(--dhtn-action-text)]' :
        color === 'gray' || color === 'text' ? 'text-[var(--dhtn-text-muted)]' :
        'text-[var(--dhtn-action-mark)]'
    return <ActivityIndicator size={px} label={label} className={ink} />
}

/**
 * DhtnRingSpinner - the larger ActivityIndicator for focused, whole-page waits
 * (payment preparing / verifying). Brand mark.
 */
export const DhtnRingSpinner = ({
    size = 'md',
    label,
}: {
    size?: 'sm' | 'md' | 'lg'
    label?: string
}) => (
    <ActivityIndicator
        size={{ sm: 24, md: 32, lg: 40 }[size]}
        label={label}
        className="text-[var(--dhtn-action-mark)]"
    />
)

// ============================================================================
// FORM COMPONENTS
// Consistent styling across all form inputs
// ============================================================================

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string
    error?: string
    helperText?: string
    focusBorder?: string
    variant?: 'light' | 'dark'
    leftIcon?: ReactNode
    rightIcon?: ReactNode
    /** Keep the label for screen readers only; the label text becomes the placeholder unless one is given. */
    hideLabel?: boolean
}

/**
 * Input - Text input with label, error, and helper text
 * - Mobile-first padding
 * - 2px border (dark-800 in dark mode)
 * - Left/right icon support
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            label,
            error,
            helperText,
            focusBorder: _focusBorder,
            variant = 'light',
            className = '',
            leftIcon,
            rightIcon,
            hideLabel = false,
            id,
            'aria-describedby': describedBy,
            ...props
        },
        ref
    ) => {
        const generatedId = React.useId()
        const inputId = id ?? generatedId
        const messageId = `${inputId}-message`
        const helperId = `${inputId}-help`
        const description = [describedBy, error && messageId, helperText && helperId].filter(Boolean).join(' ') || undefined
        const labelElement = label ? (
            <label
                htmlFor={inputId}
                className={hideLabel ? 'sr-only' : `block text-sm font-medium ${
                    variant === 'dark' ? 'text-gray-300' : 'text-[var(--dhtn-text)]'
                }`}
            >
                {label}
            </label>
        ) : null
        return (
            <div className="space-y-2">
                {!hideLabel && labelElement}
                {/* A hidden label sits inside the field box so it adds no space-y gap above the input. */}
                <div className="relative">
                    {hideLabel && labelElement}
                    {leftIcon && (
                        <div
                            className={`absolute start-4 top-1/2 transform -translate-y-1/2 ${
                                variant === 'dark' ? 'text-gray-500' : 'text-gray-400'
                            }`}
                        >
                            {leftIcon}
                        </div>
                    )}
                    <input
                        ref={ref}
                        className={`
                            w-full px-4 py-3
                            border-2 rounded-control
                            transition-none
                            focus:outline-none focus:ring-0
                            text-lg
                            placeholder:text-lg
                            ${leftIcon ? 'ps-12' : ''}
                            ${rightIcon ? 'pe-14' : ''}
                            ${props.type === 'email' ? 'font-email' : ''}
                            bg-transparent border-[var(--dhtn-border-strong)] text-[var(--dhtn-text)] placeholder-[var(--dhtn-text-muted)] focus:border-[var(--dhtn-text)]
                            ${error ? 'border-error-500 focus:border-error-500' : ''}
                            ${className}
                        `}
                        {...props}
                        // Email addresses are always Latin, left to right, even inside an RTL page.
                        dir={props.dir ?? (props.type === 'email' ? 'ltr' : undefined)}
                        placeholder={props.placeholder ?? (hideLabel ? label : undefined)}
                        id={inputId}
                        aria-invalid={error ? true : props['aria-invalid']}
                        aria-describedby={description}
                    />
                    {rightIcon && (
                        <div
                            className={`absolute end-1 top-1/2 transform -translate-y-1/2 ${
                                variant === 'dark' ? 'text-gray-500' : 'text-gray-400'
                            }`}
                        >
                            {rightIcon}
                        </div>
                    )}
                </div>
                {error ? (
                    <p
                        id={messageId}
                        role="alert"
                        className={`field-error text-sm ${variant === 'dark' ? 'text-error-400' : 'text-[var(--dhtn-error-ink)]'}`}
                    >
                        {error}
                    </p>
                ) : null}
                {helperText ? (
                    <p
                        id={helperId}
                        className={`text-sm ${
                            variant === 'dark' ? 'text-gray-400' : 'text-[var(--dhtn-text-muted)]'
                        }`}
                    >
                        {helperText}
                    </p>
                ) : null}
            </div>
        )
    }
)

Input.displayName = 'Input'

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: string
    error?: string
    helperText?: string
    variant?: 'light' | 'dark'
}

/**
 * TextArea - Multi-line text input
 * - Same styling as Input
 * - Resizable vertically
 */
export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
    ({ label, error, helperText, variant = 'light', className = '', ...props }, ref) => {
        return (
            <div className="space-y-2">
                {label && (
                    <label
                        className={`block text-sm font-medium ${
                            variant === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}
                    >
                        {label}
                    </label>
                )}
                <textarea
                    ref={ref}
                    className={`
                        w-full px-4 py-3
                        border-2 rounded-card
                        transition-none
                        focus:outline-none focus:ring-0
                        text-base
                        placeholder:text-lg
                        resize-y
                        min-h-[120px]
                        bg-transparent border-[var(--dhtn-border-strong)] text-[var(--dhtn-text)] placeholder-[var(--dhtn-text-muted)] focus:border-[var(--dhtn-text)]
                        ${error ? 'border-error-500 focus:border-error-500' : ''}
                        ${className}
                    `}
                    {...props}
                />
                {error ? (
                    <p
                        className={`text-sm ${variant === 'dark' ? 'text-error-400' : 'text-[var(--dhtn-error-ink)]'}`}
                    >
                        {error}
                    </p>
                ) : helperText ? (
                    <p
                        className={`text-sm ${
                            variant === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}
                    >
                        {helperText}
                    </p>
                ) : null}
            </div>
        )
    }
)

TextArea.displayName = 'TextArea'

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    label?: string
    error?: string
    helperText?: string
    variant?: 'light' | 'dark'
    options: { value: string; label: string }[]
}

/**
 * Select - Dropdown select input
 * - Same styling as Input
 * - Options passed as array
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
    ({ label, error, helperText, variant = 'light', className = '', options, ...props }, ref) => {
        return (
            <div className="space-y-2">
                {label && (
                    <label
                        className={`block text-sm font-medium ${
                            variant === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}
                    >
                        {label}
                    </label>
                )}
                <select
                    ref={ref}
                    className={`
                        w-full px-4 py-3
                        border-2 rounded-card
                        transition-none
                        focus:outline-none focus:ring-0
                        text-base
                        appearance-none
                        cursor-pointer
                        bg-no-repeat
                        bg-[right_1rem_center]
                        bg-[length:1.25rem_1.25rem]
                        bg-transparent border-[var(--dhtn-border-strong)] text-[var(--dhtn-text)] focus:border-[var(--dhtn-text)]
                        ${
                            variant === 'dark'
                                ? `bg-[url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2394a3b8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")]`
                                : `bg-[url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")]`
                        }
                        ${error ? 'border-error-500 focus:border-error-500' : ''}
                        ${className}
                    `}
                    {...props}
                >
                    {options.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {error ? (
                    <p
                        className={`text-sm ${variant === 'dark' ? 'text-error-400' : 'text-[var(--dhtn-error-ink)]'}`}
                    >
                        {error}
                    </p>
                ) : helperText ? (
                    <p
                        className={`text-sm ${
                            variant === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}
                    >
                        {helperText}
                    </p>
                ) : null}
            </div>
        )
    }
)

Select.displayName = 'Select'

// ============================================================================
// LINK COMPONENTS
// Styled links with hover effects
// ============================================================================

interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    children: ReactNode
    underline?: boolean
    variant?: 'light' | 'dark'
}

/**
 * Link - Styled anchor link
 * - Optional underline
 * - Hover color change
 */
export const Link = ({
    children,
    underline = true,
    variant = 'light',
    className = '',
    ...props
}: LinkProps) => {
    return (
        <a
            className={`
                ${
                    variant === 'dark'
                        ? 'text-gray-300 hover:text-white'
                        // A link is TEXT, so it takes the ink token, not the fill: 7.62:1 on the
                        // light canvas, 6.34:1 on the dark surface. Never a literal hex.
                        // No colour change on hover: the old one went primary.500 -> primary.600,
                        // and any hover tint that is not theme-split makes one of the two themes
                        // fainter on hover. `hover:underline` below is the affordance.
                        : 'text-[var(--dhtn-action-ink)]'
                }
                ${underline ? 'underline-offset-2 hover:underline' : ''}
                transition-none
                ${className}
            `}
            {...props}
        >
            {children}
        </a>
    )
}

// ============================================================================
// LAYOUT COMPONENTS
// Consistent spacing and structure
// ============================================================================

interface ContainerProps {
    children: ReactNode
    /** 'page' (default) is THE app width: every header and page wrapper uses it, so nav and
        content share one left/right edge. The other sizes are inner reading columns only. */
    size?: 'page' | 'narrow' | 'normal' | 'mid' | 'wide' | 'full'
    className?: string
}

/**
 * Container
 * - page: the shared app width, set once by --dhtn-page-max / --dhtn-page-gutter in index.css
 * - narrow / normal / mid / wide / full: inner columns placed INSIDE a page container
 */
export const Container = ({ children, size = 'page', className = '' }: ContainerProps) => {
    if (size === 'page') {
        return (
            <div data-page-width className={`dhtn-page ${className}`}>
                {children}
            </div>
        )
    }

    const sizes = {
        narrow: 'max-w-3xl',
        normal: 'max-w-5xl',
        mid: 'max-w-6xl',
        wide: 'max-w-7xl',
        full: 'max-w-full',
    }

    return (
        <div className={`mx-auto px-4 sm:px-6 lg:px-8 w-full ${sizes[size]} ${className}`}>
            {children}
        </div>
    )
}

interface CardProps {
    children: ReactNode
    className?: string
    padded?: boolean
    interactive?: boolean
    onClick?: () => void
    variant?: 'light' | 'dark'
}

/**
 * Card - Content container with border
 * - 2px border (dark-800 in dark mode)
 * - Transparent fill: the card inherits its parent's background, so ONLY the border
 *   separates it. Light already looked this way (white on white); in dark the card
 *   now matches the canvas instead of sitting on a lighter surface. Borders, never
 *   fills, carry the structure — the same rule as borders-not-shadows.
 * - Optional interactive hover state (border only, no background change)
 * - Generous padding for clean spacing
 */
export const Card = ({
    children,
    className = '',
    padded = true,
    interactive = false,
    onClick,
}: CardProps) => {
    return (
        <div
            className={twMerge(
                `border-2 rounded-card
                border-[var(--dhtn-border)] bg-transparent
                ${padded ? 'p-6 sm:p-8' : ''}
                ${interactive ? 'cursor-pointer' : ''}`,
                className
            )}
            onClick={onClick}
        >
            {children}
        </div>
    )
}

interface SectionProps {
    children: ReactNode
    className?: string
    variant?: 'light' | 'dark'
    padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl'
}

/**
 * Section - Page section with consistent padding
 * - Use for major page sections
 * - Background color support
 */
export const Section = ({
    children,
    className = '',
    variant = 'light',
    padding = 'xl',
}: SectionProps) => {
    const paddingClasses = {
        none: '',
        sm: 'py-8 sm:py-12',
        md: 'py-12 sm:py-16',
        lg: 'py-16 sm:py-20',
        xl: 'py-20 sm:py-24',
    }

    return (
        <section
            className={`
                ${paddingClasses[padding]}
                ${variant === 'dark' ? 'bg-dark-800' : 'bg-white'}
                ${className}
            `}
        >
            {children}
        </section>
    )
}

interface DividerProps {
    className?: string
    /** Accepted for existing callers; the border follows the theme through --dhtn-border. */
    variant?: 'light' | 'dark'
    orientation?: 'horizontal' | 'vertical'
}

/**
 * Divider - Visual separator
 * - 2px border
 * - Horizontal or vertical
 */
export const Divider = ({
    className = '',
    orientation = 'horizontal',
}: DividerProps) => (
    <hr
        className={`
            border-[1.5px]
            border-[var(--dhtn-border)]
            ${orientation === 'vertical' ? 'h-full w-0' : 'w-full h-0'}
            ${className}
        `}
    />
)

interface SpacerProps {
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    orientation?: 'vertical' | 'horizontal'
}

/**
 * Spacer - Consistent spacing component
 * - Use instead of hardcoded margins
 */
export const Spacer = ({ size = 'md', orientation = 'vertical' }: SpacerProps) => {
    const sizes = {
        xs: 'space-x-2',
        sm: 'space-x-4',
        md: 'space-x-6',
        lg: 'space-x-8',
        xl: 'space-x-12',
    }

    if (orientation === 'vertical') {
        const vSizes = {
            xs: 'h-2',
            sm: 'h-4',
            md: 'h-6',
            lg: 'h-8',
            xl: 'h-12',
        }
        return <div className={vSizes[size]} />
    }

    return <div className={`${sizes[size]} flex`} />
}

// ============================================================================
// FEEDBACK COMPONENTS
// Badges, alerts, progress indicators
// ============================================================================

interface BadgeProps {
    children: ReactNode
    variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary'
    mode?: 'light' | 'dark'
    size?: 'sm' | 'md'
}

/**
 * Badge - Small label/tag component
 * - Multiple color variants
 * - Rounded pill shape
 */
export const Badge = ({
    children,
    variant = 'default',
    mode = 'light',
    size = 'md',
}: BadgeProps) => {
    const lightVariants = {
        default: 'bg-gray-100 text-gray-700 border-2 border-[var(--dhtn-border-strong)]',
        success: 'bg-success-50 text-success-700 border-2 border-success-300',
        warning: 'bg-warning-50 text-warning-700 border-2 border-warning-300',
        error: 'bg-error-50 text-error-700 border-2 border-error-300',
        info: 'bg-info-50 text-info-700 border-2 border-info-300',
        primary: 'bg-primary-50 text-primary-700 border-2 border-primary-300',
    }

    const darkVariants = {
        default: 'bg-dark-800 text-gray-300 border-2 border-[var(--dhtn-border-strong)]',
        success: 'bg-success-900/30 text-success-400 border-2 border-success-700',
        warning: 'bg-warning-900/30 text-warning-400 border-2 border-warning-700',
        error: 'bg-error-100/20 text-error-400 border-2 border-error-700',
        info: 'bg-info-900/30 text-[var(--dhtn-action-ink)] border-2 border-info-700',
        primary: 'bg-primary-900/30 text-[var(--dhtn-action-ink)] border-2 border-primary-700',
    }

    const variants = mode === 'dark' ? darkVariants : lightVariants

    const sizes = {
        sm: 'px-2.5 py-1 text-xs',
        md: 'px-3 py-1.5 text-sm',
    }

    return (
        <span
            className={`inline-flex items-center font-semibold normal-case rounded ${variants[variant]} ${sizes[size]}`}
        >
            {children}
        </span>
    )
}

interface AlertProps {
    children: ReactNode
    title?: string
    variant?: 'info' | 'success' | 'warning' | 'error'
    mode?: 'light' | 'dark'
    icon?: ReactNode
}

/**
 * Alert - Notification box with color variants
 * - Optional title
 * - Optional icon
 */
export const Alert = ({ children, title, variant = 'info', mode = 'light', icon }: AlertProps) => {
    const lightVariants = {
        info: 'bg-info-50 border-2 border-info-200 text-info-800',
        success: 'bg-success-50 border-2 border-success-200 text-success-800',
        warning: 'bg-warning-50 border-2 border-warning-200 text-warning-800',
        error: 'bg-error-50 border-2 border-error-200 text-error-700',
    }

    const darkVariants = {
        info: 'bg-info-900/30 border-2 border-info-700 text-[var(--dhtn-action-ink)]',
        success: 'bg-success-900/30 border-2 border-success-700 text-success-300',
        warning: 'bg-warning-900/30 border-2 border-warning-700 text-warning-300',
        error: 'bg-error-100/20 border-2 border-error-700 text-error-400',
    }

    const variants = mode === 'dark' ? darkVariants : lightVariants

    return (
        <div className={`p-4 sm:p-5 rounded-card ${variants[variant]}`}>
            {(title || icon) && (
                <div className="flex items-center gap-3 mb-2">
                    {icon}
                    {title && <h4 className="font-bold text-base sm:text-lg">{title}</h4>}
                </div>
            )}
            <div className="text-sm sm:text-base">{children}</div>
        </div>
    )
}

interface SkeletonProps {
    className?: string
    variant?: 'light' | 'dark' | 'auto'
}

export const Skeleton = ({
    className = '',
    variant = 'auto',
}: SkeletonProps) => {
    return (
        <div
            className={`
                skel-shimmer
                rounded-card
                ${variant === 'auto' ? 'bg-[var(--dhtn-border)]' : variant === 'dark' ? 'bg-dark-700' : 'bg-gray-300'}
                ${className}
            `}
        />
    )
}

interface ProgressBarProps {
    value: number // 0-100
    size?: 'sm' | 'md' | 'lg'
    showLabel?: boolean
    label?: string
    variant?: 'light' | 'dark'
    color?: 'primary' | 'blue' | 'success' | 'warning' | 'error'
}

/**
 * ProgressBar - Progress indicator
 * - Multiple sizes
 * - Optional label with percentage
 * - Multiple color options
 * - Duolingo-style light reflection effect
 */
export const ProgressBar = ({
    value,
    size = 'md',
    showLabel = false,
    label = 'Progress',
    variant = 'light',
    color = 'primary',
}: ProgressBarProps) => {
    const sizes = {
        sm: 'h-2',
        md: 'h-4',
        lg: 'h-5',
    }

    const colors = {
        primary: 'bg-primary-500',
        blue: 'bg-blue-500',
        success: 'bg-success-500',
        warning: 'bg-warning-500',
        error: 'bg-error-500',
    }

    const clampedValue = Math.max(0, Math.min(100, value))

    return (
        <div className="space-y-2">
            {showLabel && (
                <div className="flex justify-between text-sm">
                    <span
                        className={
                            variant === 'dark'
                                ? 'text-gray-400 font-medium'
                                : 'text-gray-600 font-medium'
                        }
                    >
                        {label}
                    </span>
                    <span
                        className={variant === 'dark' ? 'font-semibold text-gray-200' : 'font-bold'}
                    >
                        {Math.round(clampedValue)}%
                    </span>
                </div>
            )}
            <div
                className={`w-full rounded-full overflow-hidden ${sizes[size]} ${
                    variant === 'dark' ? 'bg-[var(--dhtn-border-strong)]' : 'bg-gray-200'
                }`}
            >
                <div
                    className={`h-full ${colors[color]} rounded-full transition-[width] duration-700 ease-out`}
                    style={{ width: `${clampedValue}%` }}
                />
            </div>
        </div>
    )
}

// ============================================================================
// UTILITY COMPONENTS
// Helper components for common patterns
// ============================================================================

interface TooltipProps {
    content: string
    children: ReactNode
    position?: 'top' | 'bottom' | 'left' | 'right'
}

/**
 * Tooltip - Hover tooltip (simple CSS implementation)
 * Note: For production, consider using a library like Tippy.js
 */
export const Tooltip = ({ content, children, position = 'top' }: TooltipProps) => {
    const positionClasses = {
        top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
        bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
        left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
        right: 'left-full top-1/2 transform -translate-y-1/2 ml-2',
    }

    return (
        <div className="relative group inline-block">
            {children}
            <div
                className={`
                    absolute ${positionClasses[position]}
                    px-3 py-2
                    bg-gray-900 text-white
                    text-xs font-medium
                    rounded-card
                    whitespace-nowrap
                    opacity-0
                    invisible
                    group-hover:opacity-100
                    group-hover:visible
                    transition-all duration-200
                    z-50
                `}
            >
                {content}
            </div>
        </div>
    )
}

interface EmptyStateProps {
    icon: ReactNode
    title: string
    description?: string
    action?: ReactNode
    variant?: 'light' | 'dark'
}

/**
 * EmptyState - Empty state component for lists, tables, etc.
 * - Icon, title, description, optional action
 */
export const EmptyState = ({
    icon,
    title,
    description,
    action,
    variant = 'light',
}: EmptyStateProps) => (
    <div className={`text-center py-12 sm:py-16 px-6`}>
        <div
            className={`inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-control mb-6 border-[var(--dhtn-border)] ${
                variant === 'dark' ? 'bg-dark-800' : 'bg-gray-100'
            } border-2`}
        >
            {icon}
        </div>
        <H3 className={`mb-3 ${variant === 'dark' ? 'text-white' : 'text-gray-900'}`}>{title}</H3>
        {description && (
            <Body
                className={`${variant === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-8 max-w-md mx-auto`}
            >
                {description}
            </Body>
        )}
        {action && <div className="flex justify-center gap-4">{action}</div>}
    </div>
)

// ============================================================================
// MODAL / OVERLAY
// Dark-mode-aware modal with backdrop
// ============================================================================

interface ModalProps {
    children: ReactNode
    onClose: () => void
    variant?: 'light' | 'dark'
    className?: string
    maxWidth?: string
}

// ── Confirm Dialog ───────────────────────────────────────────────────────────

interface ConfirmDialogProps {
    open: boolean
    title: string
    message: string
    confirmLabel?: string
    cancelLabel?: string
    variant?: 'light' | 'dark'
    onConfirm: () => void
    onCancel: () => void
    danger?: boolean
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    title,
    message,
    variant = 'light',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    onConfirm,
    onCancel,
    danger = false,
}) => {
    const dialogRef = React.useRef<HTMLDialogElement>(null)
    const cancelRef = React.useRef<HTMLButtonElement>(null)
    const titleId = React.useId()
    const messageId = React.useId()
    React.useEffect(() => {
        const dialog = dialogRef.current
        if (!open || !dialog) return
        const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const overflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        dialog.showModal()
        cancelRef.current?.focus()
        return () => {
            dialog.close()
            document.body.style.overflow = overflow
            if (previousFocus?.isConnected) previousFocus.focus()
        }
    }, [open])
    if (!open) return null

    return (
        <dialog
            ref={dialogRef}
            aria-labelledby={titleId}
            aria-describedby={messageId}
            aria-modal="true"
            className="m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-card border-2 border-[var(--dhtn-border)] bg-[var(--dhtn-surface)] p-6 text-[var(--dhtn-text)] backdrop:bg-black/75"
            onCancel={event => { event.preventDefault(); onCancel() }}
            onKeyDown={event => {
                if (event.key !== 'Tab') return
                const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')
                const first = buttons[0]
                const last = buttons[buttons.length - 1]
                if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
                else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
            }}
            onClick={event => { if (event.target === event.currentTarget) {
                const rect = event.currentTarget.getBoundingClientRect()
                if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onCancel()
            } }}
        >
            <h2 id={titleId} className="mb-3 text-xl font-bold">{title}</h2>
            <p id={messageId} className="mb-6 text-[var(--dhtn-text-muted)]">{message}</p>
            <div className="flex flex-col gap-3 pb-1 sm:flex-row sm:justify-end">
                <SecondaryButton ref={cancelRef} variant={variant === 'dark' ? 'secondaryDark' : 'secondary'} size="sm" onClick={onCancel}>{cancelLabel}</SecondaryButton>
                <Button size="sm" variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>{confirmLabel}</Button>
            </div>
        </dialog>
    )
}

export const Modal = ({
    children,
    onClose,
    className = '',
    maxWidth = 'max-w-lg',
}: ModalProps) => {
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose()
    }

    // Token-driven solid surface (floats over the scrim). `variant` is still accepted
    // for backward compatibility but no longer drives colour.
    return (
        <div
            className="fixed inset-0 bg-black/75 z-[100] flex items-center justify-center p-4"
            onClick={handleBackdropClick}
        >
            <div
                className={twMerge(
                    `w-full ${maxWidth} rounded-card border-2 p-8 bg-[var(--dhtn-surface)] border-[var(--dhtn-border)] text-[var(--dhtn-text)]`,
                    className
                )}
            >
                {children}
            </div>
        </div>
    )
}

// ============================================================================
// DEFAULT EXPORTS
// For convenient importing
// ============================================================================

export default {
    // Typography
    Display,
    H1,
    H2,
    H3,
    H4,
    BodyXl,
    Body,
    BodySm,
    Caption,
    Label,
    Stat,
    StatLabel,
    // Buttons
    Button,
    SecondaryButton,
    // Loading
    LoadingSpinner,
    DhtnRingSpinner,
    // Forms
    Input,
    TextArea,
    Select,
    // Links
    Link,
    // Layout
    Container,
    Card,
    Section,
    Divider,
    Spacer,
    // Feedback
    Badge,
    Alert,
    Skeleton,
    ProgressBar,
    // Utility
    Tooltip,
    EmptyState,
    // Modal
    Modal,
    ConfirmDialog,
}
